import React, { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Container, Row, Col, Card, CardBody, Button, Badge, Alert } from "reactstrap"
import { checkPaymentFallback } from "../../api/requests/payment-requests"

const SubscriptionPaymentFail = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [merchantOid, setMerchantOid] = useState("")
  const [isAlreadyUsed, setIsAlreadyUsed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isInvalid, setIsInvalid] = useState(false)
  const [failMessage, setFailMessage] = useState("")

  useEffect(() => {
    const oid = searchParams.get("merchant_oid")
    const message = searchParams.get("fail_message")
    if (message) setFailMessage(decodeURIComponent(message))
    if (oid) {
      setMerchantOid(oid)
      checkStatus(oid)
    } else {
      setLoading(false)
      setIsInvalid(true)
    }
    document.title = "Abonelik Ödemesi Başarısız - Tahsilat360"
  }, [searchParams])

  useEffect(() => {
    if (!loading && !isInvalid) {
      window.history.replaceState(null, "", "/subscription/plans")
      window.history.pushState(null, "", window.location.href)
    }
  }, [loading, isInvalid])

  const checkStatus = async (oid) => {
    try {
      setLoading(true)
      const res = await checkPaymentFallback(oid)
      const data = await res.json()
      if (!res.ok) { setIsInvalid(true); return }
      if (data.data === true) setIsAlreadyUsed(true)
      else setIsAlreadyUsed(false)
    } catch {
      setIsInvalid(true)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)" }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" role="status" />
          <p>Durum kontrol ediliyor...</p>
        </div>
      </div>
    )
  }

  if (isInvalid) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} sm={10}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: 20 }}>
                <CardBody className="p-5 text-center">
                  <Alert color="danger" className="border-0 mb-4">
                    <h4 className="alert-heading">Hata!</h4>
                    <p className="mb-0">Geçersiz işlem numarası veya ödeme bulunamadı.</p>
                  </Alert>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)" }}>
      <Container className="px-3 px-sm-0">
        <Row className="justify-content-center">
          <Col lg={6} md={8} sm={10}>
            <Card className="border-0 shadow-lg" style={{ borderRadius: 20, overflow: "hidden" }}>
              <CardBody className="text-center p-4 p-sm-5">
                {/* İkon */}
                <div className="d-flex align-items-center justify-content-center mb-4 mx-auto" style={{
                  width: 100, height: 100, borderRadius: "50%",
                  background: isAlreadyUsed ? "linear-gradient(135deg, #ff9800, #f57c00)" : "linear-gradient(135deg, #ff6b6b, #ee5a24)",
                  boxShadow: isAlreadyUsed ? "0 10px 30px rgba(255,152,0,.3)" : "0 10px 30px rgba(255,107,107,.3)"
                }}>
                  <i className={`bx ${isAlreadyUsed ? "bx-info-circle" : "bx-x"} text-white`} style={{ fontSize: 48 }} />
                </div>

                <Alert color={isAlreadyUsed ? "warning" : "danger"} className="border-0 mb-4">
                  <h4 className="alert-heading mb-1">{isAlreadyUsed ? "URL Daha Önce Kullanılmış!" : "Ödeme Başarısız!"}</h4>
                  <p className="mb-0">{isAlreadyUsed ? "Bu sayfa daha önce görüntülenmiş" : "Abonelik ödemeniz tamamlanamadı"}</p>
                </Alert>

                <p className="text-muted mb-4" style={{ fontSize: 16, lineHeight: 1.6 }}>
                  {isAlreadyUsed
                    ? "Bu hata sayfası daha önce görüntülenmiştir. Ödeme durumunuz için destek ekibiyle iletişime geçin."
                    : failMessage || "Ödeme işleminiz tamamlanamadı. Lütfen kart bilgilerinizi kontrol edip tekrar deneyin."}
                </p>

                {/* İşlem Detayları */}
                <Card className="border-0 bg-light mb-4">
                  <CardBody className="p-4">
                    <h6 className="text-muted mb-3"><i className="bx bx-receipt me-2" />İşlem Detayları</h6>
                    <div className="mb-3">
                      <label className="form-label text-muted small mb-1">İşlem Numarası</label>
                      <div className="d-flex align-items-center justify-content-center">
                        <Badge color={isAlreadyUsed ? "warning" : "danger"} className="px-3 py-2 me-2" style={{ fontSize: 14, fontFamily: "'Courier New', monospace" }}>
                          {merchantOid || "Bilinmiyor"}
                        </Badge>
                        {merchantOid && (
                          <Button color="outline-danger" size="sm" onClick={() => copyToClipboard(merchantOid)}>
                            <i className="bx bx-copy" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mb-0">
                      <label className="form-label text-muted small mb-1">İşlem Tarihi</label>
                      <div className="text-dark">
                        <i className="bx bx-time-five me-2" />
                        {new Date().toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Badge color={isAlreadyUsed ? "warning" : "danger"} className="px-4 py-3 mb-4" style={{ fontSize: 16, fontWeight: 600, borderRadius: 25 }}>
                  <i className={`bx ${isAlreadyUsed ? "bx-info-circle" : "bx-x-circle"} me-2`} />
                  {isAlreadyUsed ? "URL Daha Önce Kullanılmış" : "Ödeme Başarısız"}
                </Badge>

                <div className="text-center mb-4">
                  <Button color="primary" className="w-100 w-sm-auto px-4 py-2" style={{ fontWeight: 600 }} onClick={() => navigate("/subscription/plans")}>
                    <i className="bx bx-refresh me-2" />Tekrar Dene
                  </Button>
                </div>

                <div className="mt-4 pt-4 border-top">
                  <p className="text-muted small mb-0">
                    <i className="bx bx-shield-check me-2" />
                    {isAlreadyUsed ? "Bu işlem güvenli SSL bağlantısı ile gerçekleştirilmiştir" : "Kartınızdan herhangi bir ücret çekilmemiştir"}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default SubscriptionPaymentFail
